define('threadsThreadMessageView',[
  'app',
  'module',
  'backbone',
  'marionette',
  'underscore',
  'jquery',
  'settings',
  'dateUtils',
  'template!threadsThreadMessageView'
], function (
  App,
  Module,
  Backbone,
  Marionette,
  _,
  $,
  Settings,
  DateUtils,
  Tpl
) {
  'use strict';

  function notify(message, type) {
    if (!$.notify) { return; }
    $.notify({ message: message }, {
      type: type || 'info',
      placement: { from: 'top', align: 'center' },
      delay: type === 'danger' ? 5000 : 3000
    });
  }

  Module.exports = Marionette.LayoutView.extend({
    template: Tpl,
    className: 'message-view',

    ui: {
      header: '.message-header',
      body: '.message-body',
      iframe: '.message-iframe',
      recipients: '.message-recipients',
      toggle: '.message-toggle',
      forwardBtn: '.btn-forward'
    },

    regions: {
      attachmentsRegion: '.attachments-region'
    },

    events: {
      'click @ui.header': 'onToggle',
      'click @ui.forwardBtn': 'onForward'
    },

    // The button lives inside the header, whose click toggles the message —
    // stop the bubbling so forwarding never collapses/expands it.
    onForward: function(e) {
      e.stopPropagation();
      this.trigger('message:forward', this.model);
    },

    initialize: function() {
      this.expanded = this.options.expanded || false;
    },

    onRender: function() {
      if (this.expanded) {
        this.$el.addClass('expanded');
      } else {
        this.$el.removeClass('expanded');
      }
      // Highlight the message that this thread was opened for (e.g. the email
      // a sales-inbox item is linked to) with a colored border, so it stands
      // out among the other messages in the conversation.
      var highlightId = this.options.highlightMessageId;
      this.$el.toggleClass('linked-email',
        !!highlightId && this.model && this.model.get('id') === highlightId);
    },

    onShow: function() {
      this.renderBodyIframe();
      this.renderAttachments();
    },

    /**
     * Write the email HTML into the per-message iframe so hostile CSS stays
     * sandboxed and every link gets <base target="_blank">. After the
     * document.close() we measure the body's scrollHeight and match the
     * iframe's height so the message flows inline in the thread without
     * its own scrollbar.
     */
    renderBodyIframe: function() {
      if (!this.ui.iframe || !this.ui.iframe.length) { return; }

      var rawContent = this.model.get('content') || this.model.get('contentText') || '';
      var isHtml = !!this.model.get('content');
      if (!isHtml && rawContent) {
        // Preformatted plain text → HTML-escape + preserve newlines/tabs so
        // the iframe renders it legibly.
        rawContent = _.escape(rawContent)
          .replace(/\n/g, '<br/>')
          .replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;');
      }
      var content = this.resolveInlineImages(rawContent);

      // Basic typography so unstyled emails don't render in Times New Roman
      // and `pre-wrap` keeps whitespace for plain-text fallbacks.
      var bodyCss =
        'html,body{margin:0;padding:0;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;' +
        'font-size:13px;line-height:1.5;color:#344054;word-wrap:break-word;}' +
        'a{color:#1976d2;}' +
        'blockquote{color:#667085;border-left:3px solid #e0e3e8;padding-left:10px;margin:8px 0;}' +
        'img{max-width:100%;height:auto;}' +
        'table{max-width:100%;}';

      var iframe = this.ui.iframe.get(0);
      try {
        var doc = iframe.contentWindow.document;
        doc.open();
        doc.write(
          '<!doctype html><html><head><base target="_blank">' +
          '<meta charset="utf-8"><style>' + bodyCss + '</style>' +
          '</head><body>' + content + '</body></html>'
        );
        doc.close();
      } catch (e) {
        return;
      }

      var self = this;
      var resize = function () { self.resizeIframeToContent(); };
      // Defer the first measurement to the next frame so the browser has
      // had a chance to lay out the freshly-written document — reading
      // `body.scrollHeight` synchronously right after doc.close() almost
      // always returns 0. Double rAF gives layout + paint.
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(function () {
          window.requestAnimationFrame(resize);
        });
      } else {
        setTimeout(resize, 0);
      }
      try {
        iframe.contentWindow.addEventListener('load', resize);
        var imgs = iframe.contentDocument.images || [];
        for (var i = 0; i < imgs.length; i++) {
          imgs[i].addEventListener('load', resize);
          imgs[i].addEventListener('error', resize);
        }
        // A ResizeObserver on the iframe body catches anything we missed —
        // late-loading remote CSS, lazy images, web fonts re-flowing text.
        // Keep the ref so we can disconnect it when the view is destroyed.
        if (typeof window.ResizeObserver === 'function' && iframe.contentDocument && iframe.contentDocument.body) {
          this.bodyResizeObserver = new window.ResizeObserver(resize);
          this.bodyResizeObserver.observe(iframe.contentDocument.body);
        }
      } catch (e) { /* ignore */ }
    },

    resizeIframeToContent: function() {
      if (!this.ui.iframe || !this.ui.iframe.length) { return; }
      var iframe = this.ui.iframe.get(0);
      try {
        var doc = iframe.contentDocument;
        if (!doc) { return; }
        var body = doc.body;
        var root = doc.documentElement;
        if (!body && !root) { return; }
        // Grab the tallest of body/html scrollHeight + offsetHeight — some
        // emails set `html { height:100% }` which makes body.scrollHeight
        // useless unless we also look at documentElement.
        var h = Math.max(
          body ? body.scrollHeight : 0,
          body ? body.offsetHeight : 0,
          root ? root.scrollHeight : 0,
          root ? root.offsetHeight : 0
        );
        if (h > 0) { this.ui.iframe.css('height', h + 'px'); }
      } catch (e) { /* cross-origin or detached doc */ }
    },

    onBeforeDestroy: function() {
      if (this.bodyResizeObserver) {
        try { this.bodyResizeObserver.disconnect(); } catch (e) { /* ignore */ }
        this.bodyResizeObserver = null;
      }
    },

    onToggle: function() {
      this.expanded = !this.expanded;
      if (this.expanded) {
        this.$el.addClass('expanded');
        // The iframe was invisible (display:none on .message-body) when we
        // first measured, so its body scrollHeight was 0. Re-measure now
        // that it's visible; defer one frame so the browser lays it out.
        var self = this;
        if (typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(function () { self.resizeIframeToContent(); });
        } else {
          setTimeout(function () { self.resizeIframeToContent(); }, 0);
        }
      } else {
        this.$el.removeClass('expanded');
      }
    },

    /**
     * Reuse the mails module's per-attachment view (Pdf/Image/Default preview
     * + download on click) so the UX matches the /mails page exactly.
     *
     * The mails view expects a "mailModel" with a `getDowloadUrl(mailSecId, fileName)`
     * method. We adapt the thread message model into that shape so we don't
     * have to duplicate any rendering code.
     */
    renderAttachments: function() {
      var attachments = (this.model && this.model.get('attachments')) || [];
      if (!attachments.length) { return; }

      var mailSecId = this.options.mailSecId;
      var messageId = this.model.get('id');
      if (!mailSecId || !messageId) { return; }

      // Build a minimal "mailModel" adapter: an object with id + the
      // getDowloadUrl method the AttachmentView expects.
      var adapter = new Backbone.Model({ id: messageId });
      adapter.getDowloadUrl = function (fromSecId, fileName) {
        return Settings.url('compuzz2',
          'mails/fetch/' + fromSecId + '/' + messageId + '/download/' + encodeURIComponent(fileName));
      };

      var attachmentModels = _.map(attachments, function (a) {
        return new Backbone.Model(a);
      });
      var collection = new Backbone.Collection(attachmentModels);

      var region = this.getRegion('attachmentsRegion');
      if (!region) { return; }

      var self = this;
      require(['mailsMailRightCollapsedAttachmentsView'], function (AttachmentsView) {
        var view = new AttachmentsView({
          collection: collection,
          model: adapter,
          mailSecId: mailSecId,
          // Open image / PDF attachments in an in-app preview modal (instead
          // of a new browser tab). Opt-in so the /mails page is unchanged.
          preview: true
        });
        region.show(view);
        // Tag every attachment tile with its file name, for the context menu.
        view.children.each(function (child) {
          child.$el.attr('data-file-name', child.model.get('fileName'));
        });
        self.setupAttachmentMenu(function (fileName) {
          return adapter.getDowloadUrl(mailSecId, fileName);
        });
      });
    },

    /**
     * Right click on an attachment: download it, and — when the thread is
     * shown for an inbox item (options.attachmentTargets) — file it into the
     * internal documents of one of the item's offers/orders. With several
     * candidates the entry opens a submenu to pick one.
     */
    setupAttachmentMenu: function (urlOf) {
      var self = this;
      var targets = null;
      if (this.options.attachmentTargets) {
        this.options.attachmentTargets().done(function (list) { targets = list || []; })
          .fail(function () { targets = []; });
      }

      this.$('.attachments-region').contextMenu({
        selector: '.attachment-view',
        build: function ($trigger) {
          var fileName = $trigger.attr('data-file-name');
          if (!fileName) { return false; }
          var items = {
            download: {
              name: _.i18n('mails.attachment.download', null, 'Download'),
              icon: 'fa-download',
              callback: function () { self.downloadAttachment(urlOf(fileName), fileName); }
            }
          };
          if (self.options.attachmentTargets) {
            items.sep = '---------';
            items.toDocuments = self.documentsMenuItem(targets, function (poi) {
              self.fileAttachment(urlOf(fileName), fileName, poi);
            });
          }
          return { items: items };
        }
      });
    },

    // The "internal documents" entry: loading / nothing to file into / one
    // offer (direct) / several (submenu).
    documentsMenuItem: function (targets, onPick) {
      var label = _.i18n('mails.attachment.toDocuments', null, 'Add to internal documents');
      if (targets === null) {
        return { name: label + ' (' + _.i18n('common.loading', null, 'loading…') + ')', disabled: true };
      }
      if (!targets.length) {
        return { name: label + ' — ' + _.i18n('mails.attachment.noOffer', null, 'no linked offer or order'), disabled: true };
      }
      var describe = function (t) {
        return (t.offer ? 'Offer ' : 'Order ') + t.purchaseOrderId +
          (t.productName ? ' · ' + t.productName : '') + (t.status ? ' (' + t.status + ')' : '');
      };
      if (targets.length === 1) {
        return {
          name: label + ': ' + describe(targets[0]),
          icon: 'fa-folder-open',
          callback: function () { onPick(targets[0].purchaseOrderId); }
        };
      }
      var sub = {};
      _.each(targets, function (t, i) {
        sub['t' + i] = {
          // Cited in the email first; then the customer's recent offers.
          name: describe(t) + (t.source === 'email' ? ' — ' + _.i18n('mails.attachment.inEmail', null, 'in the email') : ''),
          callback: function () { onPick(t.purchaseOrderId); }
        };
      });
      return { name: label, icon: 'fa-folder-open', items: sub };
    },

    downloadAttachment: function (url, fileName) {
      var a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },

    fileAttachment: function (url, fileName, purchaseOrderId) {
      notify(_.i18n('mails.attachment.toDocuments.saving', null, 'Adding to the internal documents…'), 'info');
      require(['entities/sales_inbox'], function () {
        App.request('salesInbox:attachment-to-documents', url, fileName, purchaseOrderId).done(function () {
          notify(fileName + ' → ' + purchaseOrderId + ': ' +
            _.i18n('mails.attachment.toDocuments.done', null, 'added to the internal documents'), 'success');
        }).fail(function () {
          notify(_.i18n('mails.attachment.toDocuments.failed', null, 'Could not add the file to the internal documents'),
            'danger');
        });
      });
    },

    serializeData: function() {
      var data = {};
      if (this.model) {
        var from = this.model.get('from');
        data.senderName = '';
        data.senderEmail = '';
        if (from && from.length > 0) {
          data.senderName = from[0].personal || from[0].address || '';
          data.senderEmail = from[0].address || '';
        }

        var to = this.model.get('to');
        data.toAddresses = '';
        if (to && to.length > 0) {
          data.toAddresses = _.map(to, function(addr) {
            return addr.personal || addr.address || '';
          }).join(', ');
        }

        var cc = this.model.get('cc');
        data.ccAddresses = '';
        if (cc && cc.length > 0) {
          data.ccAddresses = _.map(cc, function(addr) {
            return addr.personal || addr.address || '';
          }).join(', ');
        }

        data.date = DateUtils.toMDYHHMMFormat(this.model.get('sentDate'));
        // The body HTML is written into the iframe in renderBodyIframe();
        // we don't expose `content` to the template anymore.
        data.attachments = this.model.get('attachments') || [];
      }
      return data;
    },

    /**
     * Replace every `cid:<contentId>` occurrence in the HTML with either a
     * data-URI (when the attachment ships base64 inline) or the mails
     * download URL. Same rule the mailsMailMailTextView uses on the /mails
     * page — contentId on inbound emails usually arrives wrapped in angle
     * brackets (`<xyz>`), so we strip the first/last char before building
     * the `cid:xyz` needle.
     */
    resolveInlineImages: function(content) {
      if (!content) { return ''; }
      var attachments = (this.model && this.model.get('attachments')) || [];
      var mailSecId = this.options.mailSecId;
      var messageId = this.model && this.model.get('id');
      if (!attachments.length || !messageId) { return content; }

      _.each(attachments, function(attachment) {
        if (!attachment.contentId) { return; }
        var cid = attachment.contentId;
        var bare = (cid.length >= 2 && cid.charAt(0) === '<' && cid.charAt(cid.length - 1) === '>') ?
          cid.slice(1, -1) :
          cid;
        var replacement = '';
        if (attachment.base64) {
          replacement = 'data:' + attachment.contentType + ';base64,' + attachment.base64;
        } else if (mailSecId) {
          replacement = Settings.url('compuzz2',
            'mails/fetch/' + mailSecId + '/' + messageId + '/download/' + encodeURIComponent(attachment.fileName));
        } else {
          return;
        }
        // contentId legitimately contains `.` and sometimes `+`, `@`, so
        // escape regex metas before building the matcher.
        var escapedCid = bare.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        var regex = new RegExp('cid:' + escapedCid, 'g');
        content = content.replace(regex, replacement.replace(/"/g, '\''));
      });
      return content;
    }
  });
});

